import { Tag } from '@vrplatform/components';
import { useQuery } from '@vrplatform/data';
import type { Maybe } from '@vrplatform/utils';
import { Group, Skeleton } from '@mantine/core';
import { formatAddress } from '@vrplatform/ui-common';
import { ListingNameCell } from '../../_components';
import type { ListingRow } from './useListingsTableQuery';

type Props = {
  connectionId: Maybe<string>;
  name: string;
  address: ListingRow['address'];
};

export const ListingsTableNameCell = ({
  connectionId,
  address,
  name,
}: Props) => {
  const {
    data: connections = [],
    isInitialLoading,
    error,
  } = useQuery(
    (q, args) => {
      return q
        .connection({
          where: {
            id: {
              _eq: args.connectionId,
            },
          },
          limit: 1,
          order_by: [{ name: 'asc_nulls_last' }],
        })
        .map((c) => ({
          id: c.id,
          iconRound: c.icon || c.app.iconRound,
          name: c.name,
        }));
    },
    {
      skip: !connectionId,
      queryKey: ['connections'],
      variables: {
        connectionId,
      },
      keepPreviousData: true,
    }
  );

  if (error) return <Tag color="red">{error?.message || 'Failed'}</Tag>;

  if (isInitialLoading)
    return (
      <Group wrap="nowrap">
        <Skeleton height={20} width={30} />
        <Skeleton height={20} width={500} />
      </Group>
    );

  return (
    <ListingNameCell
      name={name}
      connections={connections}
      address={
        address?.full ||
        (address
          ? formatAddress({
              ...address,
              line1: address.line1 || '',
              line2: address.line2 || '',
              city: address.city || '',
              state: address.state || '',
              stateCode: address.stateCode || '',
              countryCode: address.countryCode || '',
              postcode: address.postalCode || '',
              country: '',
            })
          : undefined)
      }
      variant="compact"
    />
  );
};
